import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Alert, Input, Popconfirm, Spin, Switch, Table, Tag, Tooltip, message } from "antd";
import { DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import { ButtonItem, SelectItem, cuentaMask } from "../../../../Components/Items";

// Contenido de la fila expandida: qué cuenta contable tiene cada empleado en este
// concepto. Se pide al servidor la primera vez que el concepto se expande, no en el
// mount de la pestaña, para no disparar una petición por concepto de golpe.
class CuentasEmpleadosConcepto extends Component {
  state = {
    loading: false,
    empleados: [],
    guardandoId: null,
    busqueda: "",
    // Trae también a los activos a los que el concepto no les aplica. Sin esto los
    // conceptos de captura manual (pensión alimenticia, préstamo empresa, caja de
    // ahorro) salen con la tabla vacía: el alta de empleado sólo mete a `conceptosIds`
    // los conceptos `automaticoGlobal`, así que no están en la lista de nadie.
    verTodos: false,
  };

  componentDidMount() {
    this.consultarEmpleados();
  }

  cambiarVerTodos(verTodos) {
    this.setState({ verTodos }, () => this.consultarEmpleados());
  }

  consultarEmpleados() {
    this.setState({ loading: true });
    axios
      .post(
        "nomina/cuentasEmpleadosConcepto",
        {
          empresaId: this.props.empresaId,
          conceptoNominaId: this.props.conceptoNominaId,
          todos: this.state.verTodos,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ loading: false });
        if (res.data.success === 2) {
          const empleados = res.data.empleados || [];
          // En un concepto que ya no genera cuenta por empleado sólo tiene sentido
          // mostrar a quienes conservan una asignación de antes; el resto nunca la
          // va a tener.
          this.setState({
            empleados: this.props.soloConCuenta
              ? empleados.filter((e) => e.cuentaContableId)
              : empleados,
          });
        } else {
          this.setState({ empleados: [] });
          message.error(res.data.message || "No se pudieron cargar los empleados");
        }
      })
      .catch(() => {
        this.setState({ loading: false, empleados: [] });
        message.error("Error al comunicarse con el servidor");
      });
  }

  guardarCuenta(empleado, cuentaContableId) {
    const anterior = empleado.cuentaContableId || null;
    const nuevo = cuentaContableId || null;
    if (`${anterior || ""}` === `${nuevo || ""}`) return;

    this.setState({ guardandoId: empleado._id });
    axios
      .post(
        "nomina/editarCuentaEmpleadoConcepto",
        {
          empresaId: this.props.empresaId,
          empleadoId: empleado._id,
          conceptoNominaId: this.props.conceptoNominaId,
          cuentaContableId: nuevo,
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ guardandoId: null });
        if (res.data.success !== 2) {
          message.error(res.data.message || "No se pudo actualizar la cuenta del empleado");
          return;
        }

        const cuenta = nuevo ? this.buscarCuenta(nuevo) : null;
        this.setState((prev) => ({
          empleados: prev.empleados.map((e) =>
            e._id === empleado._id ? { ...e, cuentaContableId: nuevo, cuenta } : e
          ),
        }));

        // Solo cambia el conteo del renglón padre cuando se gana o se pierde una
        // asignación, y sólo si el empleado cuenta para el denominador: el padre muestra
        // "conCuenta / aplican", así que asignarle cuenta a uno al que el concepto no le
        // aplica dejaría un 1 / 0.
        const delta = (nuevo ? 1 : 0) - (anterior ? 1 : 0);
        if (delta !== 0 && empleado.estado === 1 && empleado.aplica && this.props.onAsignacionCambiada) {
          this.props.onAsignacionCambiada(this.props.conceptoNominaId, delta);
        }

        message.success(nuevo ? "Cuenta del empleado actualizada" : "Asignación eliminada");
      })
      .catch(() => {
        this.setState({ guardandoId: null });
        message.error("Error al comunicarse con el servidor");
      });
  }

  buscarCuenta(cuentaContableId) {
    const cuenta = (this.props.cuentasPorId || {})[cuentaContableId];
    if (!cuenta) return null;
    return { _id: cuentaContableId, codigo: cuenta.codigo, descripcion: cuenta.descripcion };
  }

  // El catálogo cubre las cuentas vigentes de la empresa; si un empleado quedó
  // apuntando a una que ya no aparece ahí, se agrega su etiqueta para que el
  // Select no muestre un ObjectId pelón.
  opcionesParaEmpleado(empleado) {
    const opciones = this.props.opcionesCuentas || [];
    if (!empleado.cuentaContableId) return opciones;
    const yaEsta = opciones.some((o) => o.value === empleado.cuentaContableId);
    if (yaEsta) return opciones;
    return [
      {
        value: empleado.cuentaContableId,
        label: empleado.cuenta
          ? `${cuentaMask(empleado.cuenta.codigo)} - ${empleado.cuenta.descripcion}`
          : "Cuenta no encontrada en el catálogo",
      },
      ...opciones,
    ];
  }

  render() {
    const { busqueda, empleados, loading, guardandoId, verTodos } = this.state;
    const { soloConCuenta, informativo } = this.props;

    const texto = busqueda.trim().toLowerCase();
    const filtrados = texto
      ? empleados.filter(
          (e) =>
            e.nombreCompleto.toLowerCase().includes(texto) ||
            `${e.codigo ?? ""}`.includes(texto) ||
            `${e.serieNumero ?? ""}`.toLowerCase().includes(texto)
        )
      : empleados;

    // Sólo cuentan los que el timbrado sí va a atender: a un empleado que no trae el
    // concepto no se le va a generar nada, así que no es un pendiente.
    const sinCuenta = empleados.filter((e) => e.estado === 1 && e.aplica && !e.cuentaContableId).length;

    const columns = [
      {
        title: "Empleado",
        dataIndex: "nombreCompleto",
        key: "nombreCompleto",
        width: "28%",
        render: (value, record) => (
          <span>
            {record.serieNumero || record.codigo ? (
              <span style={{ color: "#8c8c8c", marginRight: 6 }}>
                {record.serieNumero || record.codigo}
              </span>
            ) : null}
            {value}
          </span>
        ),
      },
      {
        title: "Estatus",
        dataIndex: "estado",
        key: "estado",
        width: "14%",
        render: (value, record) => (
          <>
            {value === 1 ? <Tag color="green">Activo</Tag> : <Tag color="orange">Baja</Tag>}
            {value === 1 && !record.aplica && (
              <Tooltip title="Este concepto no está en la lista de conceptos del empleado, así que la nómina no se lo calcula. Puedes elegirle cuenta desde aquí, pero no se usará hasta que se le asigne el concepto en Editar Empleado.">
                <Tag style={{ marginRight: 0 }}>No le aplica</Tag>
              </Tooltip>
            )}
          </>
        ),
      },
      {
        title: "Cuenta asignada",
        dataIndex: "cuentaContableId",
        key: "cuentaContableId",
        width: "43%",
        render: (value, record) => (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <SelectItem
              value={value || undefined}
              options={this.opcionesParaEmpleado(record)}
              placeholder={
                record.aplica ? "Sin cuenta — se creará al timbrar con importe" : "Sin cuenta"
              }
              showSearch
              noFocusSiguiente
              filterOption={(input, option) =>
                (option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: "100%", minWidth: 260 }}
              loading={guardandoId === record._id}
              onChange={(nuevoValor) => this.guardarCuenta(record, nuevoValor)}
            />
            {!value && !soloConCuenta && record.aplica && (
              <Tooltip title="Este empleado todavía no tiene subcuenta; se le generará automáticamente el primer timbrado en que este concepto le venga con importe. Si siempre le sale en cero, nunca se le crea ninguna.">
                <Tag color="red" style={{ marginRight: 0 }}>Sin cuenta</Tag>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: "Acción",
        key: "accion",
        width: "15%",
        render: (_, record) =>
          record.cuentaContableId ? (
            <Popconfirm
              title={
                record.aplica
                  ? "¿Quitar la asignación? La subcuenta contable no se borra; sólo se desliga del empleado y se le volverá a generar el próximo timbrado en que el concepto le venga con importe."
                  : "¿Quitar la asignación? La subcuenta contable no se borra; sólo se desliga del empleado. Como el concepto no le aplica, no se le volverá a generar ninguna."
              }
              onConfirm={() => this.guardarCuenta(record, null)}
              okText="Sí"
              cancelText="No"
            >
              <ButtonItem type="default" size="small" rojo icon={<DeleteOutlined />}>
                Quitar
              </ButtonItem>
            </Popconfirm>
          ) : null,
      },
    ];

    return (
      <Spin spinning={loading}>
        <div style={{ padding: "4px 0" }}>
          <Alert
            type={soloConCuenta || informativo ? "warning" : "info"}
            showIcon
            style={{ marginBottom: 8 }}
            message={
              informativo
                ? `Concepto informativo — ${this.props.descripcion || ""}`
                : soloConCuenta
                ? `Cuentas heredadas — ${this.props.descripcion || ""}`
                : `Cuentas por empleado — ${this.props.descripcion || ""}`
            }
            description={
              informativo ? (
                <span>
                  Este concepto tiene encendido <b>«No Suma»</b>, así que nunca aparece en la póliza
                  — pero también tiene <b>«Genera cuenta por empleado»</b>, y esa bandera sí se
                  ejecuta al timbrar: a cada empleado se le crea una subcuenta contable real que
                  después no se usa para nada, consumiendo consecutivo. Conviene apagar esa bandera
                  desde <b>Editar Concepto</b>.
                </span>
              ) : soloConCuenta ? (
                <span>
                  Este concepto ya no genera cuenta por empleado, así que estas asignaciones{" "}
                  <b>no se usan al timbrar</b>: el movimiento se va directo a la cuenta de pasivo del
                  concepto. Se muestran sólo para que puedas revisarlas o quitarlas.
                </span>
              ) : (
                <span>
                  Cambiar o quitar una cuenta <b>no modifica las pólizas ya generadas</b>; sólo afecta
                  los timbrados futuros. Los empleados sin cuenta la reciben automáticamente la
                  primera vez que se les timbre este concepto <b>con importe</b>: mientras venga en
                  cero no se les genera ninguna.
                  {sinCuenta > 0 && (
                    <>
                      {" "}
                      <b>{sinCuenta}</b> empleado(s) activo(s) todavía sin cuenta.
                    </>
                  )}
                  {verTodos && (
                    <div style={{ marginTop: 6 }}>
                      Estás viendo también empleados a los que <b>no</b> les aplica el concepto. La
                      cuenta que les pongas se guarda, pero queda sin usarse hasta que el concepto se
                      les asigne en <b>Editar Empleado → Conceptos</b> y se les capture importe; en
                      ese momento el timbrado la reutiliza en vez de crear otra.
                    </div>
                  )}
                </span>
              )
            }
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 16,
              marginBottom: 8,
            }}
          >
            <Input
              placeholder="Buscar empleado..."
              prefix={<SearchOutlined />}
              size="small"
              allowClear
              value={busqueda}
              onChange={(e) => this.setState({ busqueda: e.target.value })}
              style={{ width: 260 }}
            />

            {!soloConCuenta && (
              <span>
                <Switch
                  size="small"
                  checked={verTodos}
                  onChange={(valor) => this.cambiarVerTodos(valor)}
                />
                <Tooltip title="Un empleado se da de alta con sólo los conceptos automáticos, así que los conceptos de captura manual (pensión alimenticia, préstamo, caja de ahorro) no aparecen en la lista de nadie. Enciende esto para elegirles cuenta de todos modos.">
                  <span style={{ marginLeft: 6, borderBottom: "1px dotted #bfbfbf", cursor: "help" }}>
                    Ver todos los empleados
                  </span>
                </Tooltip>
              </span>
            )}
          </div>

          <Table
            className="custom-small-table"
            size="small"
            rowKey={(record) => record._id}
            columns={columns}
            dataSource={filtrados}
            locale={{
              emptyText: soloConCuenta
                ? "Ningún empleado conserva cuenta de este concepto"
                : verTodos
                ? "No hay empleados activos en la empresa"
                : "Ningún empleado tiene este concepto asignado. Enciende «Ver todos los empleados» si quieres elegirles cuenta de todos modos.",
            }}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} empleados`,
            }}
            scroll={{ x: 900 }}
          />
        </div>
      </Spin>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(CuentasEmpleadosConcepto);
